import { Button, Card, CircularProgress, Divider } from '@mui/material'
import { useContext, useEffect, useState } from 'react'
import { addExperimentFormFile, deleteExperimentFormFile, File, FormField } from '../../../api'
import AttachFileIcon from '../../../assets/attach_file.svg'
import DeleteIcon from '../../../assets/delete.svg'
import FileIcon from '../../../assets/file.svg'
import ErrorFileIcon from '../../../assets/file_error.svg'

import classNames from 'classnames'
import { filter, first, forEach, get, isEmpty, map } from 'lodash'
import { AppStateContext } from '../../../state/AppProvider'
import CustomDialog from '../../dialogs/CustomDialog/CustomDialog'
import { ToastMessage } from '../../ExperimentForm/ExperimentFormToast'
import styles from './FileInputFormField.module.css'

interface Props {
  data: FormField<File[]>
  fieldName: string
  stepName: string
  name: string
  convId: string
  subtitle?: string
  templateUrl?: string
  onError?: (message: Pick<ToastMessage, 'title' | 'description' | 'type'>) => void
}

export const FileInputFormField = ({
  data,
  fieldName,
  stepName,
  name,
  convId,
  subtitle,
  templateUrl,
  onError,
}: Props) => {
  const [updatedFiles, setUpdatedFiles] = useState<File[]>(data.field_value ?? [])
  const [failedFiles, setFailedFiles] = useState<globalThis.File[]>([])
  const hasError = !isEmpty(data.errors)
  const [deleteFileDialogOpen, setDeleteFileDialogOpen] = useState(false)
  const [fileToDelete, setFileToDelete] = useState<File | null>(null)

  const appStateContext = useContext(AppStateContext)

  useEffect(() => {
    setUpdatedFiles(
      get(appStateContext?.state.currentChat?.extended_data.experiment, `${stepName}.${fieldName}.field_value`) ?? [],
    )
  }, [appStateContext?.state.currentChat?.extended_data.experiment])

  async function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const files = event.target.files

    forEach(files, async file => {
      const fileToAdd: File = {
        id: '',
        filename: file.name,
        content_type: file.type,
      }
      setUpdatedFiles(prev => [...prev, fileToAdd])
      try {
        const newConversation = await addExperimentFormFile(
          stepName,
          fieldName,
          file,
          convId,
          appStateContext?.state.shouldValidateForm ?? false,
        )

        if (newConversation) {
          const messages = appStateContext?.state.currentChat?.messages || []
          newConversation.messages = messages

          appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: newConversation })
        }
      } catch (error) {
        setUpdatedFiles(prev => [...filter(prev, prevFile => prevFile.filename != fileToAdd.filename)])
        setFailedFiles(prev => [...prev, file])
        onError?.({
          title: 'Error',
          description: error instanceof Error ? error.message : 'An error occurred while uploading the file',
          type: 'error',
        })
      }
    })
  }

  const handleDelete = async (fileId: string) => {
    setDeleteFileDialogOpen(false)
    setFileToDelete(null)
    setUpdatedFiles(prev => filter(prev, file => file.id !== fileId))
    try {
      const newConversation = await deleteExperimentFormFile(
        stepName,
        fieldName,
        fileId,
        convId,
        appStateContext?.state.shouldValidateForm ?? false,
      )

      if (newConversation) {
        const messages = appStateContext?.state.currentChat?.messages || []
        newConversation.messages = messages

        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: newConversation })
      }
    } catch (error) {
      setUpdatedFiles(
        get(appStateContext?.state.currentChat?.extended_data.experiment, `${stepName}.${fieldName}.field_value`) ?? [],
      )
      onError?.({
        title: 'Error',
        description: error instanceof Error ? error.message : 'An error occurred while deleting the file',
        type: 'error',
      })
    }
  }

  const handleRetry = async (file: globalThis.File) => {
    const fileToAdd: File = {
      id: '',
      filename: file.name,
      content_type: file.type,
    }

    setUpdatedFiles(prev => [...prev, fileToAdd])
    setFailedFiles(prev => [...filter(prev, prevFile => prevFile.name !== file.name)])
    try {
      const newConversation = await addExperimentFormFile(
        stepName,
        fieldName,
        file,
        convId,
        appStateContext?.state.shouldValidateForm ?? false,
      )

      if (newConversation) {
        const messages = appStateContext?.state.currentChat?.messages || []
        newConversation.messages = messages

        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: newConversation })
      }
    } catch (error) {
      setUpdatedFiles(prev => [...filter(prev, prevFile => prevFile.filename != fileToAdd.filename)])
      setFailedFiles(prev => [...prev, file])
      onError?.({
        title: 'Error',
        description: error instanceof Error ? error.message : 'An error occurred while retrying the file upload',
        type: 'error',
      })
    }
  }

  const handleDeleteErrorFile = (fileName: string) => {
    setFailedFiles(prev => [...filter(prev, prevFile => prevFile.name !== fileName)])
  }

  const getFileNameClass = (file: File) => {
    const isDisabled = data?.disabled
    const hasUrl = !isEmpty(file.url)

    if (isDisabled) {
      return hasUrl ? styles.disabledFileNameWithUrl : styles.disabledFileName
    }
    return hasUrl ? styles.fileNameWithUrl : styles.fileName
  }

  return (
    <>
      <Card
        className={classNames(
          hasError ? styles.filesInputFormContainerError : styles.filesInputFormContainer,
          data.disabled && styles.disabled,
        )}
        id={`field_${fieldName}`}>
        <p className={styles.title}>
          {name} {hasError && <span className={styles.spanError}>{`[${first(data.errors)}]`}</span>}
        </p>

        {subtitle && <p className={hasError ? styles.subtitleError : styles.subtitle}>{subtitle}</p>}

        <Divider className={styles.divider} />

        <div className={styles.buttonsContainer}>
          <Button
            className={styles.fileInputButton}
            startIcon={<img src={AttachFileIcon} />}
            disabled={data.disabled}>
            Select Files
            <input
              type="file"
              onChange={e => handleChange(e)}
              multiple
              className={styles.hiddenInput}
            />
          </Button>
          {templateUrl && (
            <>
              <Divider className={styles.buttonsDivider} />

              <Button className={styles.downloadTemplatesButton}>
                Download Templates{' '}
                <a
                  href={templateUrl}
                  className={styles.hiddenLink}
                  target="_blank"
                />
              </Button>
            </>
          )}
        </div>

        {map(updatedFiles, file => (
          <div className={styles.fileContainer}>
            {isEmpty(file.id) ? (
              <CircularProgress size={16} />
            ) : (
              <img
                src={FileIcon}
                className={styles.fileIcon}
              />
            )}
            <div className={styles.fileNameContainer}>
              <span
                className={getFileNameClass(file)}
                onClick={() => {
                  if (!isEmpty(file.url)) {
                    window.open(file.url, '_blank')
                  }
                }}>
                {file.filename}
              </span>
            </div>
            <Button
              className={styles.deleteButton}
              disabled={data.disabled}
              onClick={() => {
                setFileToDelete(file)
                setDeleteFileDialogOpen(true)
              }}>
              <img src={DeleteIcon} />
            </Button>
          </div>
        ))}

        {map(failedFiles, file => (
          <div className={styles.fileContainer}>
            <img
              src={ErrorFileIcon}
              className={styles.fileIcon}
            />
            <div className={styles.fileNameContainer}>
              <p className={styles.fileName}>{file.name}</p>
            </div>
            <div className={styles.retryContainer}>
              <Button
                className={styles.retryButton}
                onClick={() => handleRetry(file)}>
                Retry
              </Button>
              <Button
                className={styles.deleteButton}
                disabled={data.disabled}
                onClick={() => handleDeleteErrorFile(file.name)}>
                <img src={DeleteIcon} />
              </Button>
            </div>
          </div>
        ))}
      </Card>

      <CustomDialog
        open={deleteFileDialogOpen}
        setClose={() => {
          setDeleteFileDialogOpen(false)
          setFileToDelete(null)
        }}
        primaryAction={() => fileToDelete && handleDelete(fileToDelete.id)}
        primaryActionButtonText="Delete"
        secondaryAction={() => {
          setDeleteFileDialogOpen(false)
          setFileToDelete(null)
        }}
        secondaryActionButtonText="Cancel"
        title="Delete File">
        <div className={styles.deleteFileDialogContent}>
          <span>Are you sure you want to delete file</span>
          <span className={styles.deleteFileName}>{fileToDelete?.filename}</span>
          <span>?</span>
        </div>
      </CustomDialog>
    </>
  )
}
